import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from '../legacy-ball/table-management/designTokens';

// ── Interfaces ────────────────────────────────────────────────

interface ReviewerArticle {
  submissionId: string;
  title: string;
  status: string;
  reviewed: boolean;
}

interface ReviewerSummary {
  memberId: string;
  firstName: string;
  lastName: string;
  totalAssigned: number;
  reviewedCount: number;
  pendingCount: number;
  articles: ReviewerArticle[];
}

interface UnassignedArticle {
  submissionId: string;
  title: string;
  authorName: string;
  status: string;
}

interface ReviewerDistributionData {
  reviewers: ReviewerSummary[];
  unassignedArticles: UnassignedArticle[];
}

// ── Status Colors (matching WeekendBookletPage) ───────────────

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Draft: { bg: '#f3f4f6', text: '#6b7280' },
  Submitted: { bg: '#dbeafe', text: '#1d4ed8' },
  UnderReview: { bg: '#fef3c7', text: '#d97706' },
  RevisionRequested: { bg: '#fee2e2', text: '#dc2626' },
  Approved: { bg: '#dcfce7', text: '#16a34a' },
  ReadyForLayout: { bg: '#f3e8ff', text: '#9333ea' },
};

// ── Utility ───────────────────────────────────────────────────

function truncateTitle(title: string, maxLen = 80): string {
  if (title.length <= maxLen) return title;
  return title.slice(0, maxLen) + '…';
}

function useIsMobile(breakpoint = 640): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '1rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '1rem',
    borderRadius: '6px',
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  retryBtn: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    padding: '0.4rem 0.8rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: '0.85rem',
    color: TEXT_DARK,
    backgroundColor: BG_SURFACE,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
  },
  thNumeric: {
    padding: '0.75rem 1rem',
    textAlign: 'right' as const,
    fontWeight: 600,
    fontSize: '0.85rem',
    color: TEXT_DARK,
    backgroundColor: BG_SURFACE,
    borderBottom: `2px solid ${BORDER_LIGHT}`,
  },
  row: {
    cursor: 'pointer',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    transition: 'background-color 0.15s',
  },
  td: {
    padding: '0.7rem 1rem',
    fontSize: '0.9rem',
    color: TEXT_DARK,
  },
  tdNumeric: {
    padding: '0.7rem 1rem',
    fontSize: '0.9rem',
    color: TEXT_DARK,
    textAlign: 'right' as const,
  },
  chevron: {
    display: 'inline-block',
    transition: 'transform 0.2s',
    marginRight: '0.5rem',
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
  expandedPanel: {
    padding: '0.5rem 1rem 1rem 2.5rem',
    backgroundColor: BG_SURFACE,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  articleItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.4rem 0',
    fontSize: '0.85rem',
    color: TEXT_DARK,
  },
  badge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  sectionHeading: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginTop: '2rem',
    marginBottom: '0.75rem',
    borderBottom: `2px solid #c9a84c`,
    paddingBottom: '0.25rem',
  },
  unassignedItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.88rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
};

// ── Mobile Card Styles ────────────────────────────────────────

const mobileStyles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    marginBottom: '0.75rem',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.85rem 1rem',
    cursor: 'pointer',
    minHeight: '48px',
  },
  cardName: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.92rem',
    fontWeight: 500,
    color: TEXT_DARK,
    flex: 1,
  },
  cardStats: {
    display: 'flex',
    gap: '0.6rem',
    fontSize: '0.78rem',
    color: TEXT_MUTED,
  },
  statPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    padding: '0.15rem 0.45rem',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  expandedPanelMobile: {
    padding: '0.5rem 0.75rem 0.75rem',
    backgroundColor: BG_SURFACE,
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  articleItemMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  articleTitleMobile: {
    fontSize: '0.84rem',
    color: TEXT_DARK,
    lineHeight: 1.3,
  },
  unassignedItemMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.3rem',
    padding: '0.7rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  unassignedMetaMobile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
};

// ── Component ─────────────────────────────────────────────────

export function ReviewerDistributionTab() {
  const [data, setData] = useState<ReviewerDistributionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const isMobile = useIsMobile();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<ReviewerDistributionData>('/weekend-booklet/reviewer-distribution');
      setData(result);
    } catch (err: any) {
      if (err?.statusCode === 403) {
        setError('Access denied. Organizer role or higher is required to view reviewer distribution.');
      } else {
        setError('Failed to load reviewer distribution data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRow = (memberId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, memberId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleRow(memberId);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading reviewer distribution…</div>;
  }

  if (error) {
    return (
      <div style={styles.error}>
        <span>{error}</span>
        <button style={styles.retryBtn} onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { reviewers, unassignedArticles } = data;

  // ── Mobile Layout ─────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={styles.container}>
        {reviewers.length === 0 ? (
          <div style={styles.emptyState}>No reviewers assigned yet.</div>
        ) : (
          reviewers.map((reviewer) => {
            const isExpanded = expandedRows.has(reviewer.memberId);
            const panelId = `reviewer-panel-${reviewer.memberId}`;
            return (
              <div key={reviewer.memberId} style={mobileStyles.card}>
                <div
                  style={mobileStyles.cardHeader}
                  onClick={() => toggleRow(reviewer.memberId)}
                  onKeyDown={(e) => handleRowKeyDown(e, reviewer.memberId)}
                  tabIndex={0}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                >
                  <div style={mobileStyles.cardName}>
                    <span
                      style={{
                        ...styles.chevron,
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      ▶
                    </span>
                    {reviewer.firstName} {reviewer.lastName}
                  </div>
                  <div style={mobileStyles.cardStats}>
                    <span style={{ ...mobileStyles.statPill, backgroundColor: '#e0e7ff', color: '#3730a3' }}>
                      {reviewer.totalAssigned}
                    </span>
                    <span style={{ ...mobileStyles.statPill, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                      {reviewer.reviewedCount}✓
                    </span>
                    <span style={{ ...mobileStyles.statPill, backgroundColor: '#fef3c7', color: '#d97706' }}>
                      {reviewer.pendingCount}⏳
                    </span>
                  </div>
                </div>
                {isExpanded && (
                  <div id={panelId} style={mobileStyles.expandedPanelMobile}>
                    {reviewer.articles.length === 0 ? (
                      <div style={{ color: TEXT_MUTED, fontSize: '0.84rem', padding: '0.4rem 0' }}>
                        No articles assigned.
                      </div>
                    ) : (
                      reviewer.articles.map((article, idx) => {
                        const badgeColor = article.reviewed
                          ? { bg: '#dcfce7', text: '#16a34a' }
                          : { bg: '#fef3c7', text: '#d97706' };
                        return (
                          <div
                            key={article.submissionId}
                            style={{
                              ...mobileStyles.articleItemMobile,
                              ...(idx === reviewer.articles.length - 1 ? { borderBottom: 'none' } : {}),
                            }}
                          >
                            <span style={mobileStyles.articleTitleMobile}>
                              {truncateTitle(article.title, 60)}
                            </span>
                            <span
                              style={{
                                ...styles.badge,
                                backgroundColor: badgeColor.bg,
                                color: badgeColor.text,
                                alignSelf: 'flex-start',
                              }}
                            >
                              {article.reviewed ? 'Reviewed' : 'Pending'}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Unassigned Articles Section — Mobile */}
        <h3 style={styles.sectionHeading}>Unassigned Articles</h3>
        {unassignedArticles.length === 0 ? (
          <div style={styles.emptyState}>All articles have been assigned to reviewers.</div>
        ) : (
          <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', overflow: 'hidden' }}>
            {unassignedArticles.map((article) => {
              const statusColor = STATUS_COLORS[article.status] || { bg: '#f3f4f6', text: '#6b7280' };
              return (
                <div key={article.submissionId} style={mobileStyles.unassignedItemMobile}>
                  <span style={{ fontWeight: 500, fontSize: '0.88rem', color: TEXT_DARK }}>
                    {article.title}
                  </span>
                  <div style={mobileStyles.unassignedMetaMobile}>
                    <span style={{ color: TEXT_MUTED, fontSize: '0.8rem' }}>
                      by {article.authorName}
                    </span>
                    <span
                      style={{
                        ...styles.badge,
                        backgroundColor: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {article.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Desktop Layout ────────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* Reviewer Summary Table */}
      {reviewers.length === 0 ? (
        <div style={styles.emptyState}>No reviewers assigned yet.</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th scope="col" style={styles.th}>Reviewer Name</th>
              <th scope="col" style={styles.thNumeric}>Total Assigned</th>
              <th scope="col" style={styles.thNumeric}>Reviewed</th>
              <th scope="col" style={styles.thNumeric}>Pending</th>
            </tr>
          </thead>
          <tbody>
            {reviewers.map((reviewer) => {
              const isExpanded = expandedRows.has(reviewer.memberId);
              const panelId = `reviewer-panel-${reviewer.memberId}`;
              return (
                <React.Fragment key={reviewer.memberId}>
                  <tr
                    style={styles.row}
                    onClick={() => toggleRow(reviewer.memberId)}
                    onKeyDown={(e) => handleRowKeyDown(e, reviewer.memberId)}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                  >
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.chevron,
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      >
                        ▶
                      </span>
                      {reviewer.firstName} {reviewer.lastName}
                    </td>
                    <td style={styles.tdNumeric}>{reviewer.totalAssigned}</td>
                    <td style={styles.tdNumeric}>{reviewer.reviewedCount}</td>
                    <td style={styles.tdNumeric}>{reviewer.pendingCount}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0 }}>
                        <div id={panelId} style={styles.expandedPanel}>
                          {reviewer.articles.length === 0 ? (
                            <div style={{ color: TEXT_MUTED, fontSize: '0.85rem', padding: '0.5rem 0' }}>
                              No articles assigned.
                            </div>
                          ) : (
                            reviewer.articles.map((article) => {
                              const badgeColor = article.reviewed
                                ? { bg: '#dcfce7', text: '#16a34a' }
                                : { bg: '#fef3c7', text: '#d97706' };
                              return (
                                <div key={article.submissionId} style={styles.articleItem}>
                                  <span>{truncateTitle(article.title)}</span>
                                  <span
                                    style={{
                                      ...styles.badge,
                                      backgroundColor: badgeColor.bg,
                                      color: badgeColor.text,
                                    }}
                                  >
                                    {article.reviewed ? 'Reviewed' : 'Pending'}
                                  </span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Unassigned Articles Section */}
      <h3 style={styles.sectionHeading}>Unassigned Articles</h3>
      {unassignedArticles.length === 0 ? (
        <div style={styles.emptyState}>All articles have been assigned to reviewers.</div>
      ) : (
        <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', overflow: 'hidden' }}>
          {unassignedArticles.map((article) => {
            const statusColor = STATUS_COLORS[article.status] || { bg: '#f3f4f6', text: '#6b7280' };
            return (
              <div key={article.submissionId} style={styles.unassignedItem}>
                <div>
                  <span style={{ fontWeight: 500 }}>{article.title}</span>
                  <span style={{ color: TEXT_MUTED, marginLeft: '0.75rem', fontSize: '0.82rem' }}>
                    by {article.authorName}
                  </span>
                </div>
                <span
                  style={{
                    ...styles.badge,
                    backgroundColor: statusColor.bg,
                    color: statusColor.text,
                  }}
                >
                  {article.status}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
